import { jwtDecode } from 'jwt-decode';
import type { DecodedAuthToken } from '../../components/authentication/types/AuthTypes';
import type { AuthToken } from '../../components/authentication/types/AuthTypes';
import { atlasAuthApi } from '../api/atlasAuthApi';

const ADMIN_CAPABILITY_CACHE_KEY = 'atlas_adminCapability';
export const ADMIN_CAPABILITY_EVENT = 'atlas-admin-capability-updated';

let adminCapabilityCache: boolean | null = null;

function readSessionCache(): boolean | null {
  try {
    const raw = sessionStorage.getItem(ADMIN_CAPABILITY_CACHE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // Ignore storage access issues.
  }
  return null;
}

function writeSessionCache(value: boolean): void {
  try {
    sessionStorage.setItem(ADMIN_CAPABILITY_CACHE_KEY, value ? 'true' : 'false');
  } catch {
    // Ignore storage access issues.
  }
}

function emitCapabilityChanged(value: boolean): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ADMIN_CAPABILITY_EVENT, { detail: { value } }));
}

export function getCachedAdminCapability(): boolean | null {
  if (adminCapabilityCache !== null) return adminCapabilityCache;
  adminCapabilityCache = readSessionCache();
  return adminCapabilityCache;
}

function setCachedAdminCapability(value: boolean): void {
  const previous = getCachedAdminCapability();
  adminCapabilityCache = value;
  writeSessionCache(value);
  if (previous !== value) {
    emitCapabilityChanged(value);
  }
}

export function hasAdminJwtClaim(
  decoded: Pick<DecodedAuthToken, 'is_admin' | 'role'> | null | undefined
): boolean {
  if (!decoded) return false;
  return decoded.is_admin === true || decoded.role === 'admin';
}

export function resolveIsAdminFromSources(
  decoded: Pick<DecodedAuthToken, 'is_admin' | 'role'> | null | undefined,
  cachedCapability: boolean | null
): boolean {
  // API authorization is authoritative when known.
  if (cachedCapability === false) return false;
  if (hasAdminJwtClaim(decoded)) return true;
  if (cachedCapability === true) return true;
  return false;
}

export function decodeAuthTokenClaims(token: AuthToken | null): DecodedAuthToken | null {
  if (!token?.access_token) return null;
  try {
    return jwtDecode<DecodedAuthToken>(token.access_token);
  } catch {
    return null;
  }
}

export async function probeAdminCapability(force = false): Promise<boolean> {
  const cached = getCachedAdminCapability();
  if (!force && cached !== null) {
    return cached;
  }

  try {
    // Lightweight authorization probe: 200 => admin, 403/401 => not admin.
    await atlasAuthApi.get('/admin/customers');
    setCachedAdminCapability(true);
    return true;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 403) {
      setCachedAdminCapability(false);
      return false;
    }
    const fallback = getCachedAdminCapability();
    if (fallback !== null) {
      return fallback;
    }
    setCachedAdminCapability(false);
    return false;
  }
}

export async function refreshAdminCapability(): Promise<boolean> {
  return probeAdminCapability(true);
}
